import { faSignInAlt, faSignOutAlt, faTh, faThList, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Audible } from 'components/Audible';
import { Application } from 'openfin/_v2/api/application/application';
import { useContext, useEffect, useState } from 'react';
import { keyframes } from 'styled-components';
import { colors, styled } from '../../assets/theme';
import { ThemeControl } from '../../components/ThemeControl/themeControl';
import OpenfinContext from '../../openfin/OpenfinContext';
import { makeChannelProvider } from '../../openfin/OpenfinService'
import { LayoutService } from '../../openfin/workspaces/WorkspaceUtils';
import { deinitializeStore, subscribeListener, unsubscribeListener } from '../../redux/ably/sagas';
import ConnectionBadge from '../ConnectionBadge';

const fin = (window as any).fin

const LaunchBar = () => {
  const openfin = useContext(OpenfinContext);
  const [authenticated, setAuthenticated] = useState<boolean | undefined>();

  useEffect(() => {
    subscribeListener('store:authenticated', 1, ({ data }) => {
      setAuthenticated(data.isAuthenticated);
    });

    makeChannelProvider('appLauncher', [{ name:'open', cb:(config) => openfin.openWindow(config)}])

    LayoutService.getInstance().hydrateWindows('last');
    return () => {
      unsubscribeListener('store:authenticated', 1)
    }
  }, [openfin])

  const toggleWorkspaceMenu = () => {
    openfin.openWindow({
      defaultHeight: 400,
      defaultWidth: 300,
      name: 'workspaces',
      url: '/workspace-menu',
    });
  };

  const toggleMenu = async () => {

    const ofApp: Application = await (window as any).fin.Application.getCurrent();
    const openWindows = await ofApp.getChildWindows();
    const menuWindow = openWindows.find(win => {if (win.identity.name) { return win.identity.name.includes('menu')} return false;});
    if (menuWindow) {
      menuWindow.close();
    } else {
      const launchbar = await fin.desktop.Application.getCurrent();
      const wrappedLaunchbar = await fin.Application.wrap({ uuid: launchbar.uuid });
      const openfinWindow = await wrappedLaunchbar.getWindow()
      const launchbarBounds = await openfinWindow.getBounds();
      const launchbarTopPosition = launchbarBounds.top;
      const menuTopPosition = launchbarTopPosition + 55;
      const launchbarSidePosition = launchbarBounds.left;
      const menuSidePosition = launchbarSidePosition + 35;

      const config = {
        autoShow: true,
        defaultHeight: 88,
        defaultLeft: menuSidePosition,
        defaultTop: menuTopPosition,
        defaultWidth: 625,
        maxHeight: 88,
        maxWidth: 625,
        name: `menu${Math.random() * 10 ** 18}`,
        resizable: false,
        url: '/menu',
      };

      const win = await openfin.openWindow(config);
      win.show();
      const mainWindow = fin.desktop.Application.getCurrent().getWindow();

      mainWindow.joinGroup(win);
    }
  };

  const handleQuit = () => {
    LayoutService.getInstance().persistWindows('last').then(() => {
      window.close();
    });
  }

	const logout = async () => {
    deinitializeStore();
    if (localStorage.getItem('token')) {
      LayoutService.getInstance().closeAllWindows();
      localStorage.removeItem('token');
    } else {

    const config = {
      defaultHeight: 550,
      defaultWidth: 600,
      maxHeight: 550,
      maxWidth: 600,
      name: 'login',
      resizable: false,
      saveWindowState: false,
      url: '/login',
      waitForPageLoad: true,
    };
    await openfin.openWindow(config);

    }
	};

  return (
    <Container>
      <LogoContainer>
        <LogoWrapper>
          <svg xmlns="http://www.w3.org/2000/svg" width="55" height="29">
            <g id="Styleguide" fill="none" fill-rule="evenodd" stroke="none" stroke-width="1">
              <g id="Core-Branding" transform="translate(-149,-513)">
                <g id="mountain-logo-solo" transform="matrix(1,0,0,1,137,503)">
                  <path id="Rectangle" d="M 0 0 h 78 v 48 H 0 z"/>
                  <path id="Path" fill="#ffffff" d="M 12 38.9 L 23.7 22.4 l 2.5 2.2 L 38.2 10 l 9.2 12.6 l 6.6 -6.1 L 67 38.5 L 54 20.6 l -5 4.9 l 4.8 8.9 l -15.2 -19.3 L 26.7 29.5 l -2.1 -1.8 a 20286.4 20286.4 0 0 1 -12.4 11.2 c -0.2 0.2 -0.2 0.2 -0.2 0 z"/>
                </g>
              </g>
            </g>
          </svg>
        </LogoWrapper>
      </LogoContainer>
      <GhostButton disabled={!authenticated} title="Add widget" onClick={toggleMenu}>
        <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faTh} />
      </GhostButton>
      <GhostButton disabled={!authenticated} title="Load/Save Workspace" onClick={toggleWorkspaceMenu}>
        <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faThList} />
      </GhostButton>
      <SearchBoxContainer />
      <StyledConnectionBadge />
      <GhostButton title={authenticated ? 'Log out' : 'Log in' } onClick={logout}>
      <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={authenticated === false ? faSignInAlt : faSignOutAlt} />
      </GhostButton>
      <GhostButton>
        <Audible />
      </GhostButton>
      <GhostButton>
        <ThemeControl />
      </GhostButton>
      <GhostButton title="Quit" accent={colors.accents.bad} onClick={handleQuit}>
        <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faTimes} />
      </GhostButton>
    </Container>
  );
};

export default LaunchBar;

const LogoWrapper = styled.div`
  #Path {
    fill: ${({ theme }) => theme.core.textColor};
  }
`;

const GhostButton = styled.button<{ accent?:any}>`

  border: none;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ accent }) => accent ? accent.base : 'initial'};
  &:focus {
    outline: none;
  }

  :hover {
    cursor: pointer;
  }
  :disabled {
    background-color: gray;
  }
`;

const StyledConnectionBadge = styled(ConnectionBadge)`
  align-self: center;
  justify-self: end;
`;


const SearchBoxContainer = styled.div``;

const blink = keyframes`
  50% { border-color: #ff0000; }
`;

const Container = styled.div`

  background-color: ${({ theme }) => theme.core.darkBackground};
  display: grid;
  grid-auto-flow: column;
  grid-gap: 1em;
  /* grid-template-columns: 86px 100px 1fr; */
  height: 44px;
  /* border: solid 4px transparent;
  animation-name: ${blink} ;
  animation-duration: .5s ;
  animation-timing-function: step-end ;
  animation-iteration-count: infinite ;
  animation-direction: alternate ; */
`;

const LogoContainer = styled.div`
  background-color: ${({ theme }) => theme.core.lightBackground};
  -webkit-app-region: drag;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 86px;
  :active,
  :focus,
  :hover {
    cursor: move;
  }
`;
